<template>
  <div>
    <div v-if="$auth.ready()">
      <!-- configuring sidebar only if user is authenticated -->
      <div v-if="$auth.check()">
        <v-app dark>
          <navigation-drawer v-if="!hideSidebar"></navigation-drawer>
          <toolbar></toolbar>
          <v-main :style="mainPaddingStyle">
            <v-container container--fluid :class="hideSidebar ? `pa-8` : ``">
              <router-view></router-view>
              <notification></notification>
            </v-container>
          </v-main>
          <v-footer padless v-if="license && !license.synpseCloud" :class="getLicenseFooterColour">
            <v-col
              class="text-center caption"
              cols="12"              
            >
              <span v-if="getLicenseDaysRemaining > 0" :class="getLicenseFooterTextColour">
                Licensed for '{{ license.organization }}' | Expires: {{ license.validUntil | ymd }} | License ID: {{ license.id }}        
              </span>
              <span v-else :class="getLicenseFooterTextColour">
                Licensed for '{{ license.organization }}' | License expired on: {{ license.validUntil | ymd }} | License ID: {{ license.id }} | Functionality is limited
              </span>
              <span v-if="getLicenseDaysRemaining < 30" :class="getLicenseFooterTextColour">
                | Contact hello@synpse.net for renewal 
              </span>
            </v-col>
          </v-footer>
          <SSHTerminalPanel></SSHTerminalPanel>
        </v-app>
      </div>
      <!-- for unauthenticated users - do not add fixed sidebar -->
      <div v-else>
        <v-app>
          <toolbar></toolbar>
          <v-main>
            <v-container container--fluid class="mb-14">
              <router-view></router-view>
              <notification></notification>
            </v-container>
          </v-main>
        </v-app>
      </div>
    </div>
  </div>
</template>

<script>
import NavigationDrawer from './components/NavigationDrawer';
import Toolbar from './components/Toolbar';
import Notification from './components/Notification';
import SSHTerminalPanel from './components/ssh/SSHTerminalPanel';

import moment from 'moment'

export default {
  name: 'App',

  components: {
    NavigationDrawer,
    Toolbar,
    Notification,
    SSHTerminalPanel
  },

  data: () => ({
    //
  }),

  mounted() {
    this.initializeLicense();
    this.$auth
      .load()
      .then(() => {
        if (this.$auth.check()) {
          // Getting current user
          this.$store.dispatch('GetUser').then(() => {
            this.initializeCrisp()
          })
          // Initializing project
          this.initializeProject()          
        }
      })
  },

  watch: {
    'selectedProjectId'() {
      this.loadProjectNamespaces()
    },
    '$route'(to) {
      document.title = to.meta.title || 'Synpse'
    }
  },

  computed: {
    isSshPanelVisible() {
      return this.$store.getters.isSshPanelVisible
    },
    sshPanelState() {
      return this.$store.state.sshSessions.panelState || {}
    },
    mainPaddingStyle() {
      if (!this.isSshPanelVisible) {
        return {}
      }
      if (this.sshPanelState.isMinimized) {
        return { paddingBottom: '40px' }
      }
      if (this.sshPanelState.isFullscreen) {
        return {}
      }
      const height = this.sshPanelState.height || 440
      return { paddingBottom: `${height}px` }
    },
    license() {
      return this.$store.state.license.license
    },
    currentRouteName() {
        return this.$route.name;
    },
    hideSidebar() {
      return this.$route.meta.hideSidebar ? true : false
    },
    getLicenseDaysRemaining() {
      const eventdate = moment(this.$store.state.license.license.validUntil);
      const todaysdate = moment();
      const diffDays = eventdate.diff(todaysdate, 'days');
      return diffDays
    },
    getLicenseFooterTextColour() {
      if (!this.$store.state.license.license.validUntil) {
        return ''
      }

      const diffDays = this.getLicenseDaysRemaining 
      if (diffDays < 2) {
        return ''
      }
      if (diffDays < 30) {
        return 'black--text'
      }
      return ''
    },
    getLicenseFooterColour() {
      if (!this.$store.state.license.license.validUntil) {
        return ''
      }

      const diffDays = this.getLicenseDaysRemaining 
      if (diffDays < 2) {
        return 'red darken-3'
      }
      if (diffDays < 30) {
        return 'amber lighten-1'
      }
      return ''
    },
    selectedProjectId() {
      return this.$store.state.project.selectedProjectId
    },
    projectMemberships() {
      return this.$store.state.project.memberships
    },
    selectedNamespace() {
      return this.$store.state.namespace.selectedProjectId
    },
  },

  methods: {
    async initializeLicense() {
      await this.$store.dispatch('GetLicense')
    },
    async initializeCrisp() {
      if (this.$auth.check()) {
        window.$crisp.push(['set', 'user:email', this.$store.state.user.user?.email])
        window.$crisp.push(['set', 'user:nickname', this.$store.state.user.user?.name])
      }
    },
    async initializeProject() {
      await this.$store.dispatch('GetProjectMemberships')
      this.$store.dispatch('LoadSelectedProject')
      if (!this.selectedProjectId) {
        let first = this.$store.getters.getFirstProject()
        this.$store.dispatch('SelectProject', first)
      }

      if (this.projectMemberships.length === 1) {
        // If project has no devices and no applications,
        // let's do onboarding
        if (this.projectMemberships[0].project.applicationCount === 0 && this.projectMemberships[0].project.deviceCount === 0) {
          this.$router.push({ name: 'onboarding' })
        }
      } else if (this.projectMemberships.length > 0) {                
        // Loading namespaces to populate the navigation drawer
        // on the left side
        await this.loadProjectNamespaces()               
      }
    },
    async loadProjectNamespaces() {
      let q = {
        project: this.selectedProjectId
      }
      await this.$store.dispatch('ListNamespaces', q)
      this.$store.dispatch('LoadSelectedNamespace')
    }
  }
};
</script>
